import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import classNames from 'classnames'
import PageWithModal from 'components/Page/PageWithModal'
import Image from 'components/Image'
import { Row } from 'components/Layout'
import NTMButton from 'components/Buttons/NTMButton'
import NTMTable from 'components/Table/NTMTable'
import ConfigurationModal from 'components/Modal/ConfigurationModal'
import { useGetConfigurations, useGetSensors, useGetAllDevices } from "../../service/Agrismart2Service"
import { AGRISMART2, deleteElem, post, put, getErrorMessage } from 'service/Client'
import styles from "./_agrismart.module.scss"
import { editIcon, editIcon_over, deleteIcon, deleteIcon_over } from 'img/icons'


const SlotDescriptor = ({ index, object: s }) => <Row style={{ flexWrap: "nowrap" }}>
    <div className={classNames(styles.configurationManagerSlotLabel)} >
        {`SLOT${index}`}
    </div>
    <>{
        !!s ? (
            <Row className={classNames(styles.configurationManagerSlotDescriptor)}>
                <div>{s.id}</div>
                <div>{s.name}</div>
                <div>{s.model}</div>
            </Row>
        ) : (
            <div>-</div>
        )
    }</>
</Row>


const ConfigurationManager = () => {
    const [configurationModalOpen, setConfigurationModalOpen] = useState(false)
    const [configurationModalValue, setConfigurationModalValue] = useState()
    const [message, setMessage] = useState()
    const [errorMessage, setErrorMessage] = useState()

    const queryClient = useQueryClient()
    const { configurations } = useGetConfigurations()
    const { sensors } = useGetSensors()
    const { devices } = useGetAllDevices()

    configurations.sort((a, b) => a.id - b.id)
    sensors.sort((a, b) => a.id - b.id)

    function getDevicesByConfigurationId(configurationId) {
        if (configurationId == null) return []
        return devices.filter(d => d.configurationId === configurationId).map(d => d.device)
    }

    function handleError(error) {
        setErrorMessage(getErrorMessage(error))
    }

    function submitConfiguration(configuration) {
        if (configuration.id) {
            return put(`${AGRISMART2}/configurations/${configuration.id}`, { body: configuration })
                .then(response => {
                    let newCode = response.data.code
                    setMessage(`Configurazione modificata con successo.\nIl nuovo codice è ${newCode}`)
                    queryClient.invalidateQueries('configurations')
                })
                .catch(handleError)
                .finally(() => setConfigurationModalOpen(false))
        } else {
            return post(`${AGRISMART2}/configurations`, { body: configuration })
                .then(response => {
                    let newCode = response.data.code
                    setMessage(`Creata nuova configurazione con codice ${newCode}`)
                    queryClient.invalidateQueries('configurations')
                })
                .catch(handleError)
                .finally(() => setConfigurationModalOpen(false))
        }
    }

    function deleteConfiguration(configuration) {
        return deleteElem(`${AGRISMART2}/configurations/${configuration.id}`)
            .then(() => queryClient.invalidateQueries('configurations'))
            .catch(handleError)
    }

    return (
        <PageWithModal
            message={message}
            error={errorMessage}
            clear={() => { setMessage(null); setErrorMessage(null) }}
        >
            <Row style={{ justifyContent: 'right' }}>
                <NTMButton primary
                    onClick={() => {
                        setConfigurationModalValue(null)
                        setConfigurationModalOpen(true)
                    }}
                >AGGIUNGI</NTMButton>
            </Row>
            <Row>
                <NTMTable
                    title="CONFIGURAZIONI"
                    header={[
                        {
                            Header: "ID",
                            accessor: 'id',
                        },
                        {
                            Header: "CODICE",
                            accessor: 'code',
                            filterable: true,
                        },
                        {
                            Header: "DESCRIZIONE",
                            accessor: 'description',
                            Cell: props => <div style={{ textAlign: "left", whiteSpace: "normal" }}>
                                {props.original.description}
                            </div>
                        },
                        {
                            Header: "SLOT",
                            accessor: 'slot',
                            Cell: props => <div style={{ textAlign: "left", whiteSpace: "normal" }}>
                                <SlotDescriptor index={1} object={props.original.slot1} />
                                <SlotDescriptor index={2} object={props.original.slot2} />
                                <SlotDescriptor index={3} object={props.original.slot3} />
                                <SlotDescriptor index={4} object={props.original.slot4} />
                                <SlotDescriptor index={5} object={props.original.slot5} />
                                <SlotDescriptor index={6} object={props.original.slot6} />
                                <SlotDescriptor index={7} object={props.original.slot7} />
                                <SlotDescriptor index={8} object={props.original.slot8} />
                            </div>
                        },
                        {
                            Header: "CENTRALINA",
                            accessor: 'device',
                            Cell: props => {
                                const deviceIds = getDevicesByConfigurationId(props.original.id)
                                return deviceIds.map(d => <div key={d}>{d}</div>)
                            }
                        },
                        {
                            Header: "",
                            accessor: 'actions',
                            Cell: props => <Row>
                                <Image
                                    src={editIcon}
                                    hover={editIcon_over}
                                    onClick={() => {
                                        setConfigurationModalValue(props.original)
                                        setConfigurationModalOpen(true)
                                    }}
                                />
                                &nbsp;&nbsp;
                                <Image
                                    src={deleteIcon}
                                    hover={deleteIcon_over}
                                    onClick={() => deleteConfiguration(props.original)}
                                />
                            </Row>
                        },
                    ]}
                    body={configurations}
                />
            </Row>
            <Row>
                <NTMTable
                    title="SENSORI"
                    header={[
                        {
                            Header: "ID",
                            accessor: 'id',
                            filterable: true,
                        },
                        {
                            Header: "NOME",
                            accessor: 'name',
                            filterable: true
                        },
                        {
                            Header: "MODELLO",
                            accessor: 'model',
                            filterable: true
                        },
                        {
                            Header: "DESCRIZIONE",
                            accessor: 'description',
                            Cell: props => <div style={{ textAlign: "left", whiteSpace: "normal" }}>
                                {props.original.description}
                            </div>
                        },
                    ]}
                    body={sensors}
                />
            </Row>

            <ConfigurationModal
                isOpen={configurationModalOpen}
                onClose={() => {
                    setConfigurationModalOpen(false)
                    setConfigurationModalValue(null)
                }}
                submitConfiguration={submitConfiguration}
                initialConfiguration={configurationModalValue}
                sensors={sensors}
            />
        </PageWithModal>
    )
}

export default ConfigurationManager